import { useEffect } from 'react';
import { Title, Container, Group, useMantineTheme, Box } from '@mantine/core';
import { DragDropContext, DropResult, Droppable } from '@hello-pangea/dnd';
import DndCard from '@components/dnd-card';
import { Choice, Question } from '@generated/graphql';
import classes from './questionnaire-board.module.css';

interface ColumnData {
  id?: string;
  key: string;
  name: string;
  items: Choice[];
}

const reorder = (list: Choice[], startIndex: number, endIndex: number) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);

  return result;
};

const moveColumn = (boardState: ColumnData[], dropResult: DropResult) => {
  // there is no mean to have have a destination as it checked in the onDragEnd
  const sInd = boardState.findIndex((column) => column.key === dropResult.source.droppableId);
  const dInd = boardState.findIndex((column) => column.key === dropResult.destination?.droppableId);
  const sourceIndex = dropResult.source.index;
  const destinationIndex = dropResult.destination ? dropResult.destination.index : 0;

  const sourceClone = Array.from(boardState[sInd].items);
  const destClone = Array.from(boardState[dInd].items);
  const [removed] = sourceClone.splice(sourceIndex, 1);
  destClone.splice(destinationIndex, 0, removed);

  // need a deep copy or copy w/o items.
  const newState = JSON.parse(JSON.stringify(boardState)) as ColumnData[];
  newState[sInd].items = [...sourceClone];
  newState[dInd].items = [...destClone];
  return newState;
};

type State = ColumnData[]; // Replace YourStateType with the type of your state
type StateSetter = React.Dispatch<React.SetStateAction<State>>;
type StateHook = [State, StateSetter];

interface QuestionnaireProps {
  title: string;
  question: Question;
  stateHook: StateHook;
  setNumPicked: React.Dispatch<React.SetStateAction<number>>;
}

function QuestionnaireBoard({ question, title, stateHook, setNumPicked }: QuestionnaireProps) {
  const theme = useMantineTheme();
  const [boardState, setBoardState] = stateHook;

  const onDragEnd = (dropResult: DropResult) => {
    const { source, destination } = dropResult;

    // dropped outside the list
    if (!destination) {
      return;
    }

    // withing the same column
    if (source.droppableId === destination.droppableId) {
      const sInd = boardState.findIndex((column) => column.key === source.droppableId);
      const items = reorder(boardState[sInd].items, source.index, destination.index);
      const newState = [...boardState];
      newState[sInd].items.splice(0, Infinity, ...items);
      setBoardState(newState);
    } else {
      const result = moveColumn(boardState, dropResult);
      setBoardState(result);
    }
  };

  useEffect(() => {
    if (setNumPicked) {
      setNumPicked(boardState[1].items.length);
    }
  }, [boardState, setNumPicked]);

  return (
    <Container fluid>
      <Box bg="var(--mantine-color-voyTitleBox-6)" p={10} mb={30} mt={20}>
        <Title order={2} c="white" ta="center" className={classes.title}>
          {title}
        </Title>
      </Box>
      <Container size="sm">
        <Title order={3} pt={40} className={classes.directions}>
          {question.directions}
        </Title>
      </Container>

      <Container size="md">
        <DragDropContext onDragEnd={onDragEnd}>
          <Group justify="center" align="top" grow>
            {boardState.map((column, columnIndex) => (
              <Droppable droppableId={column.key} key={column.key}>
                {(provided) => (
                  <div {...provided.droppableProps} ref={provided.innerRef}>
                    <Title size="h3" bg={theme.primaryColor} mb={20} pt={5} pb={5} className={classes.columnTitle}>
                      {column.name}
                    </Title>
                    {column.items.map((item, index) => (
                      <DndCard cardDetails={{ index, key: item.text, text: item.text, columnIndex }} key={item.text} />
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            ))}
          </Group>
        </DragDropContext>
      </Container>
    </Container>
  );
}

export default QuestionnaireBoard;
export type { ColumnData };
